import { useAuth } from "@/context/auth";
import type { components } from "@/types/api/api-types";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:7000";

// Reuse the generated OpenAPI types so the auth-user shape stays a single
// source of truth with the rest of the app (see src/context/auth.ts).
export type AuthUser = components["schemas"]["AuthUserDto"];
export type AuthResponse = components["schemas"]["AuthResponseDto"];

export type PasswordSecondFactorRequired = {
  requires_2fa: true;
  method: "password";
  challenge_token: string;
};

export type PasswordSecondFactorStatus = {
  enabled: boolean;
  updated_at: string | null;
};

type WrappedResponse<T> = {
  statusCode: number;
  data: T;
  error: boolean;
  errorData: Record<string, unknown> | null;
};

/**
 * Error thrown by the second-factor endpoints. It preserves the HTTP `status`
 * and the backend error `code` (e.g. "invalid_current_password",
 * "invalid_second_factor_challenge") so callers can tell a wrong password from
 * an expired challenge from a transient failure — instead of collapsing every
 * failure into one misleading message.
 */
export class SecondFactorApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = "SecondFactorApiError";
    this.status = status;
    this.code = code;
  }
}

export function isPasswordSecondFactorRequired(
  value: unknown,
): value is PasswordSecondFactorRequired {
  return (
    !!value &&
    typeof value === "object" &&
    "requires_2fa" in value &&
    (value as { requires_2fa?: unknown }).requires_2fa === true &&
    (value as { method?: unknown }).method === "password" &&
    typeof (value as { challenge_token?: unknown }).challenge_token === "string"
  );
}

function extractErrorCode(
  body: WrappedResponse<unknown> | null,
): string | null {
  const raw = body?.errorData?.message ?? body?.errorData?.error;
  if (Array.isArray(raw)) {
    return typeof raw[0] === "string" ? raw[0] : null;
  }
  return typeof raw === "string" ? raw : null;
}

async function readJsonResponse<T>(response: Response): Promise<T> {
  const body = (await response
    .json()
    .catch(() => null)) as WrappedResponse<T> | null;

  if (!response.ok || !body) {
    const code = extractErrorCode(body);
    throw new SecondFactorApiError(
      code || response.statusText || "Request failed",
      response.status,
      code,
    );
  }

  return body.data;
}

/**
 * Mirrors the shared api-client 401 middleware (src/lib/api-client.ts): a 401
 * on an authenticated request means a stale/expired token, so clear the session
 * to avoid a "ghost" logged-in state with a dead JWT. A wrong current-password
 * is ALSO a 401 but is a domain error — never log the user out for that.
 */
async function logoutOnStaleSession(error: unknown): Promise<never> {
  if (
    error instanceof SecondFactorApiError &&
    error.status === 401 &&
    error.code !== "invalid_current_password"
  ) {
    await useAuth.getState().logout();
  }
  throw error;
}

export async function completePasswordSecondFactor(input: {
  challenge_token: string;
  password: string;
}): Promise<AuthResponse> {
  // Public endpoint (pre-auth): its 401s are always domain codes
  // (invalid_second_factor_challenge / invalid_second_factor_password),
  // so it deliberately does NOT go through the stale-session logout.
  const response = await fetch(`${API_URL}/auth/login/password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  return readJsonResponse<AuthResponse>(response);
}

/**
 * Lifts the 2FA gate on an already-authenticated session that predates
 * enforcement: submits the second-factor password with the current session
 * token (no wallet re-sign) and returns an upgraded token + user.
 */
export async function verifyPasswordSecondFactor(
  password: string,
): Promise<AuthResponse> {
  const token = useAuth.getState().token;
  const response = await fetch(
    `${API_URL}/auth/password-second-factor/verify`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ password }),
    },
  );

  return readJsonResponse<AuthResponse>(response);
}

export async function getPasswordSecondFactorStatus(): Promise<PasswordSecondFactorStatus> {
  const token = useAuth.getState().token;
  try {
    const response = await fetch(`${API_URL}/auth/password-second-factor`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    return await readJsonResponse<PasswordSecondFactorStatus>(response);
  } catch (error) {
    return logoutOnStaleSession(error);
  }
}

export async function updatePasswordSecondFactor(input: {
  current_password?: string;
  new_password: string;
}): Promise<PasswordSecondFactorStatus> {
  const token = useAuth.getState().token;
  try {
    const response = await fetch(`${API_URL}/auth/password-second-factor`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(input),
    });

    return await readJsonResponse<PasswordSecondFactorStatus>(response);
  } catch (error) {
    return logoutOnStaleSession(error);
  }
}
